import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MyBetsVisualizationTrackingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { DsButtonKind, DsButtonVariant } from '@frontend/ui/button';

import {
    AutoCashoutResponse,
    AutoCashoutState,
    CashoutSettingsBetInfo,
    CashoutSettingsType,
    IAutoCashoutBetslipNumbers,
    RemoveAutoCashoutRequest,
} from '../../../../my-bets-base/models/early-payout';
import { AutoCashoutTrackingService } from '../../../services/auto-cash-out-tracking-service';
import { AutoCashoutService } from '../../../services/auto-cash-out.service';
import { CashoutSettingsService } from '../../../services/cash-out-settings.service';
import { MyBetsStrategy } from '../../../services/my-bets-display.strategy';

@Component({
    selector: 'ms-my-bets-earlypayout-remove-autocashout-button',
    templateUrl: 'my-bets-earlypayout-remove-autocashout-button.html',
})
export class MyBetsEarlyPayoutRemoveAutocashoutButtonComponent implements OnInit {
    @Input() betNumbers: IAutoCashoutBetslipNumbers;
    @Input() removeType: CashoutSettingsType;
    @Input() betTypeInfo: string;
    state: AutoCashoutState;
    removeError: string | null = null;
    btnClass: string;
    btnText: string;
    private betInfo: CashoutSettingsBetInfo | null;

    readonly buttonThemeKind: ThemeOptions<DsButtonKind> = {
        'mgm-4': 'primary',
        'sports-interaction': 'primary',
        'betboo-br': 'tertiary',
        'sportingbet-br': 'tertiary',
    };

    readonly buttonThemeVariant: ThemeOptions<DsButtonVariant> = { 'betboo-br': 'outline', 'sportingbet-br': 'outline' };
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private autoCashoutService: AutoCashoutService,
        private cashoutSettingsService: CashoutSettingsService,
        private autoCashoutTrackingService: AutoCashoutTrackingService,
        private myBetsStrategy: MyBetsStrategy,
        private mybetsVisualizationTrackingConfig: MyBetsVisualizationTrackingConfig,
    ) {
        this.autoCashoutTrackingService.myBetsTrackSufix = this.myBetsStrategy.inColumn ? 'Mini_MyBets' : 'Main_MyBets';
    }

    ngOnInit(): void {
        this.cashoutSettingsService.errorsCleared.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((betid: string) => this.clearErrors(betid));
        this.betInfo = this.cashoutSettingsService.getBetInfo(this.betNumbers.betslipId);
        this.setState(AutoCashoutState.Available);

        if (!this.mybetsVisualizationTrackingConfig.isEnabled) {
            this.betTypeInfo = this.betInfo?.betType ?? '';
        }
    }

    private clearErrors(betId: string): void {
        if (betId === this.betNumbers.betslipId) {
            this.removeError = null;
        }
    }

    async submitRemoveAutoCashout($event?: Event | null): Promise<void> {
        if (this.betInfo?.earlyPayout) {
            const valueWithoutStake =
                this.removeType === CashoutSettingsType.Auto
                    ? this.betInfo.earlyPayout.autoCashOutValueWithoutStake
                    : this.betInfo.earlyPayout.autoCashoutNotificationValueWithoutStake;

            const autoCashoutValue =
                this.removeType === CashoutSettingsType.Auto
                    ? this.betInfo.earlyPayout.autoCashOutValue
                    : this.betInfo.earlyPayout.autoCashoutNotificationValue;

            this.autoCashoutTrackingService.trackRemoveClick(
                this.removeType,
                this.betNumbers.betslipId,
                this.betTypeInfo,
                this.betInfo.isFreeBet,
                this.betInfo.containsLiveBets,
                valueWithoutStake || autoCashoutValue,
            );
        }

        if (this.state === AutoCashoutState.Processing) {
            return;
        }

        this.removeError = null;

        const request: RemoveAutoCashoutRequest = {
            betNumber: this.betNumbers.betslipRealId,
            autoCashoutType: Number(this.removeType),
        };

        this.setState(AutoCashoutState.Processing);

        this.autoCashoutService
            .removeEarlyPayoutAmount(request)
            .then((response: AutoCashoutResponse | null) => {
                this.handleAutoCashoutRemoveSuccess(response, request.autoCashoutType);
            })
            .catch((error: Error) => {
                this.handleAutoCashoutErrors(null);
            });
    }

    private handleAutoCashoutRemoveSuccess(response: AutoCashoutResponse | null, autoCashoutType: CashoutSettingsType): void {
        const valueWithoutStake =
            this.removeType === CashoutSettingsType.Auto
                ? this.betInfo?.earlyPayout?.autoCashOutValueWithoutStake
                : this.betInfo?.earlyPayout?.autoCashoutNotificationValueWithoutStake;

        const autoCashoutValue =
            this.removeType === CashoutSettingsType.Auto
                ? this.betInfo?.earlyPayout?.autoCashOutValue
                : this.betInfo?.earlyPayout?.autoCashoutNotificationValue;

        if (!response || !response.setAutoCashoutErrors || response.setAutoCashoutErrors.length) {
            this.handleAutoCashoutErrors(response);

            return;
        }
        this.setState(AutoCashoutState.Available);
        this.cashoutSettingsService.updateAutoCashOutSettings(this.betNumbers.betslipId, 0, autoCashoutType);

        if (!this.betInfo || !this.betInfo.earlyPayout) {
            return;
        }

        this.autoCashoutTrackingService.trackRemoveSuccess(
            this.removeType,
            this.betNumbers.betslipId,
            this.betTypeInfo,
            this.betInfo.isFreeBet,
            this.betInfo.containsLiveBets,
            valueWithoutStake || autoCashoutValue,
        );
    }

    private handleAutoCashoutErrors(response: AutoCashoutResponse | null): void {
        this.removeError =
            this.removeType === CashoutSettingsType.Auto
                ? this.sitecore.earlyPayoutErrors.RemoveAutoCashoutGeneralError
                : this.sitecore.earlyPayoutErrors.RemoveAutoCashoutNotificationGeneralError;
        this.setState(AutoCashoutState.Available);
    }

    private setState(state: AutoCashoutState): void {
        switch (state) {
            case AutoCashoutState.Available:
                this.btnClass = 'allowed';
                this.btnText =
                    this.removeType === CashoutSettingsType.Auto
                        ? this.sitecore.earlyPayoutV2.RemoveAutocashoutButtonSetText
                        : this.sitecore.earlyPayoutV2.RemoveAutocashoutNotificationButtonSetText;
                break;
            case AutoCashoutState.Processing:
                this.btnClass = 'processing';
                this.btnText = this.sitecore.earlyPayoutV2.SetAutocashoutButtonInProgressText;
                break;
        }

        this.state = state;
    }
}
